// apps/admin/src/pages/companyDetailsChanges.ts
// The Edit Company modal sends updateCompanyDetails ONLY the fields the
// founder changed: a blank-but-untouched field must not be cleared, and the
// audit row must not list fields nobody touched.

export const EDITABLE_COMPANY_FIELDS = ['companyName', 'legalName', 'abn', 'acl', 'email', 'phone'] as const;
export type EditableCompanyField = (typeof EDITABLE_COMPANY_FIELDS)[number];
export type CompanyDetailsForm = Record<EditableCompanyField, string>;

/**
 * The form values as loaded from a company doc (companyName falls back to the
 * legacy `name`). Always strings: a legacy number (e.g. a numeric abn) shows as
 * its digits, and any other non-string as ''.
 */
export function companyDetailsFormFrom(company: Partial<Record<EditableCompanyField | 'name', unknown>>): CompanyDetailsForm {
  const str = (v: unknown) => (typeof v === 'string' ? v : typeof v === 'number' && Number.isFinite(v) ? String(v) : '');
  return {
    companyName: str(company.companyName) || str(company.name),
    legalName: str(company.legalName),
    abn: str(company.abn),
    acl: str(company.acl),
    email: str(company.email),
    phone: str(company.phone),
  };
}

export type CompanyDetailsChanges =
  | { kind: 'none' }
  | { kind: 'invalid'; message: string }
  | { kind: 'changes'; fields: Partial<CompanyDetailsForm> };

/**
 * The trimmed fields whose value differs from the loaded one. A cleared field
 * is sent as '' (the callable deletes it). companyName can never be cleared.
 */
export function companyDetailsChanges(form: CompanyDetailsForm, loaded: CompanyDetailsForm): CompanyDetailsChanges {
  // The ABN compares and is sent as digits only, like the server stores it, so
  // retyping it with spaces is not a change (no write, no audit row).
  const norm = (field: EditableCompanyField, v: string) => (field === 'abn' ? v.replace(/\s+/g, '') : v.trim());
  const fields: Partial<CompanyDetailsForm> = {};
  for (const field of EDITABLE_COMPANY_FIELDS) {
    const next = norm(field, form[field]);
    if (next !== norm(field, loaded[field])) fields[field] = next;
  }
  if (fields.companyName === '') return { kind: 'invalid', message: 'Company name cannot be empty' };
  return Object.keys(fields).length === 0 ? { kind: 'none' } : { kind: 'changes', fields };
}
